(async (algolia) => {

  if (!customElements.get('cart-drawer-recommend')) {

    const { relatedProducts, frequentlyBoughtTogether } = await import('@algolia/recommend-js/dist/esm/index');
    const { default: recommend } = await import('@algolia/recommend/dist/recommend.esm.browser.js');
    const { productCardHtml, slidesHtml, sliderHtml } = await import('./modules/algolia-recommend-component.js');

    class CartDrawerRecommend extends HTMLElement {
      constructor() {
        super();

        // Initialize Algolia Recommend
        this.recommendClient = recommend(
          algolia.config.app_id,
          algolia.config.search_api_key,
        );

        // State for the current recommendations
        this.state = {
          'related-products': {
            items: [],
            timestamp: null
          },
          'frequently-bought-together': {
            items: [],
            timestamp: null
          }
        }

        // Initialize recommendations when the browser resize
        this.screenType = window.innerWidth < 768 ? 'mobile' : 'desktop';
        window.addEventListener('resize', () => {
          if (!document.querySelector('body').classList.contains('cart-drawer-is-open')) {
            return;
          }
          if (window.innerWidth < 768 && this.screenType !== 'mobile') {
            this.screenType = 'mobile';
            this.classList.add('reload');
            this.initRecommendations();
          }
          if (window.innerWidth >= 768 && this.screenType !== 'desktop') {
            this.screenType = 'desktop';
            this.classList.add('reload');
            this.initRecommendations();
          }
        });

        // Initialize Swym objects if they don't exist (same as collection component)
        if (typeof window !== 'undefined') {
          const swymObjects = ['SwymViewProducts', 'SwymWatchProducts', 'SwymProductVariants'];
          swymObjects.forEach(k => window[k] || (window[k] = {}));
        }

        // Initialize wishlist functionality
        this.initializeWishlist();

        // If the element has the loading class, initialize the recommendations directly
        if (this.classList.contains('reload')) {
          this.initRecommendations();
        }

      }

      async initRecommendations() {
        // Only initialize when the element is not loaded with the latest data
        if (this.classList.contains('loaded') && !this.classList.contains('reload')) {
          return;
        }

        // Remove the loading class
        this.classList.remove('reload');

        // This get the current cart
        const cartItemIds = await this.getCartItemIds();
        if (!cartItemIds.variant.length) {
          this.classList.add('empty-cart');
          return;
        } else {
          this.classList.remove('empty-cart');
        }

        // Only initialize if the element is visible on the screen
        const type = this.getAttribute('data-type') || 'desktop';
        if (type === 'desktop' && this.screenType == 'mobile') {
          this.classList.add('not-in-view');
          return;
        }
        if (type === 'mobile' && this.screenType == 'desktop') {
          this.classList.add('not-in-view');
          return;
        }
        this.classList.remove('not-in-view');


        // Initialize the frequently bought together
        this.initFrequentlyBoughtTogether(cartItemIds);

        // Initialize the related products
        this.initRelatedProducts(cartItemIds);

      }

      /**
       * Initialize the related products algolia component
       * @param {object} cartItemIds
       * @returns {void}
       */
      initRelatedProducts(cartItemIds) {

        // Check if the element exists
        const recommendEl = this;
        const productsEl = this.querySelector('[data-related-products]');
        const productsLoaderEl = this.querySelector('[data-related-products-loader]');
        const productsNoResultsEl = this.querySelector('[data-related-products-no-results]');
        if (!productsEl) return;
        if (!this.recommendClient) return;

        // Options
        let options = {
          indexName: `${algolia.config.index_prefix}products`,
          itemsToShow: 10,
          confidenceScore: 0, // 0-100 (0 = low confidence, 100 = high confidence)
          randomize: true,
          sliderButtons: false,
          slideMultipleSlides: true,
        }

        // Get options from element data and combine with this.options
        if (productsEl?.dataset?.options) {
          const dataOptions = parseJson(productsEl.dataset.options);
          options = {
            ...options,
            ...dataOptions
          };
        }

        // Required options to initialize
        if (!options.indexName) {
          return;
        }

        // Format options
        options.itemsToShow = parseInt(options.itemsToShow);
        options.confidenceScore = parseFloat(options.confidenceScore);

        // Get the variables for algolia recommend
        const { recommendClient } = this;
        const { indexName , itemsToShow, randomize, confidenceScore, sliderButtons, slideMultipleSlides } = options;

        // Use custom approach similar to main related products
        this.fetchAndRenderRelatedProducts(cartItemIds, productsEl, productsLoaderEl, productsNoResultsEl, recommendEl, options);

      }

      /**
       * Initialize the related products algolia component
       * @param {object} cartItemIds
       * @returns {void}
       */
      initFrequentlyBoughtTogether(cartItemIds) {

        // Check if the element exists
        const recommendEl = this;
        const productsEl = this.querySelector('[data-bought-together]');
        const productsWrapperEl = this.querySelector('[data-bought-together-wrapper]');
        const productsLoaderEl = this.querySelector('[data-bought-together-loader]');
        const productsNoResultsEl = this.querySelector('[data-bought-together-no-results]');
        if (!productsEl) return;
        if (!this.recommendClient) return;

        // Options
        let options = {
          indexName: `${algolia.config.index_prefix}products`,
          itemsToShow: 10,
          confidenceScore: 0, // 0-100 (0 = low confidence, 100 = high confidence)
          randomize: true,
          sliderButtons: false,
          slideMultipleSlides: true,
        }

        // Get options from element data and combine with options
        if (productsEl?.dataset?.options) {
          const dataOptions = parseJson(productsEl.dataset.options);
          options = {
            ...options,
            ...dataOptions
          };
        }

        // Required options to initialize
        if (!options.indexName) {
          return;
        }

        // Format options
        options.itemsToShow = parseInt(options.itemsToShow);
        options.confidenceScore = parseFloat(options.confidenceScore);

        // Get the variables for algolia recommend
        const { recommendClient } = this;
        const { indexName , itemsToShow, randomize, confidenceScore, sliderButtons, slideMultipleSlides } = options;

        // Use custom approach similar to main related products
        this.fetchAndRenderFrequentlyBoughtTogether(cartItemIds, productsEl, productsLoaderEl, productsNoResultsEl, recommendEl, options);

      }

      /**
       * Get the current cart item ids
       * @returns {Promise<string[]>} Returns an array of cart item ids
       */
      async getCartItemIds() {
        const cart = await fetch('/cart.js').then(response => response.json());
        if (!cart.items) return [];
        const cartVariantIds = cart.items.filter(item => !item.properties._free_gift || item.properties._free_gift == false).map(item => String(item.variant_id).trim());
        const cartProductIds = cart.items.filter(item => !item.properties._free_gift || item.properties._free_gift == false).map(item => parseInt(item.product_id));

        return {
          variant: cartVariantIds,
          product: cartProductIds,
        };
      }

      /**
       * Custom fetch and render method for related products
       */
      async fetchAndRenderRelatedProducts(cartItemIds, productsEl, productsLoaderEl, productsNoResultsEl, recommendEl, options) {
        const { indexName, itemsToShow, randomize, confidenceScore, sliderButtons, slideMultipleSlides } = options;
        try {
          const timestamp = Date.now();
          let allItems = [];
          const seenProductIds = new Set(); // Track product IDs we've already added

          // Loop through cart items to get recommendations until we have enough
          for (let i = 0; i < cartItemIds.variant.length && allItems.length < itemsToShow; i++) {
            const variantId = cartItemIds.variant[i];

            const response = await this.recommendClient.getRelatedProducts([{
              indexName,
              objectID: variantId,
              maxRecommendations: 60,
              threshold: confidenceScore,
              queryParameters: {
                filters: "inventory_available:true"
              }
            }], {
              // Add cache busting headers
              headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
              }
            });

            let items = response.results[0].hits || [];

            // Sort the items by score
            items = items.sort((a, b) => b._score - a._score);

            // Filter out items already in cart and items we've already added
            items = items.filter(item => {
              // Exclude items already in cart
              if (cartItemIds && cartItemIds.product && cartItemIds.product.includes(item.id)) {
                return false;
              }
              // Exclude items we've already added from previous cart items
              if (seenProductIds.has(item.id)) {
                return false;
              }
              return true;
            });

            // Add new items to our collection
            items.forEach(item => {
              seenProductIds.add(item.id);
              allItems.push(item);
            });

            // Stop early if we have enough items
            if (allItems.length >= itemsToShow) {
              break;
            }
          }

          let items = allItems;

          // Sort all items by score (items from earlier cart items may have higher scores)
          items = items.sort((a, b) => b._score - a._score);

          // Save the items in the state
          this.state['related-products'] = {
            items,
            timestamp: timestamp
          }

          // Ensure we have enough items after filtering
          if (items.length < itemsToShow) {
            // Not enough items available after filtering
          }

          // Limit the number of items
          items = items.slice(0, itemsToShow);

          // Always shuffle the items to get different results
          // Use a more sophisticated shuffle to ensure different results
          for (let i = items.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [items[i], items[j]] = [items[j], items[i]];
          }

          // Remove the loader and show/hide the no results message
          productsLoaderEl.classList.add('hidden');
          if (items.length === 0) {
            productsNoResultsEl.classList.remove('hidden');
          } else {
            recommendEl.classList.add('loaded')
            productsNoResultsEl.classList.add('hidden');
          }

          // Render products using custom approach
          this.renderRelatedProducts(items, productsEl, sliderButtons, slideMultipleSlides);

        } catch (error) {
          console.error('Error fetching related products:', error);
          productsLoaderEl.classList.add('hidden');
          productsNoResultsEl.classList.remove('hidden');
        }
      }

      /**
       * Custom render method for related products
       */
      renderRelatedProducts(items, container, sliderButtons, slideMultipleSlides) {
        // Clear existing content
        container.innerHTML = '';

        // Create a proper HTML template function
        const html = (strings, ...values) => {
          return strings.reduce((result, string, i) => {
            return result + string + (values[i] || '');
          }, '');
        };

        // Create the slider structure
        const sliderHtml = `
          <slider-component class="algolia-recommend" data-options='{
            "moveMultipleSlides": ${slideMultipleSlides},
            "offset": 24,
            "mobileOffset": 24
          }'>
            <div class="slider related-products">
              <div class="slider__container">
                <div class="slider__wrapper slider__wrapper--no-scroll-bar ${items.length <= 2 ? 'slider__wrapper--default-cursor' : ''}" data-scroller-wrapper>
                  <div class="slider__content slider__content--full related-products-items" data-scroller-content>
                    ${items.map(item => {
                      const productHtml = productCardHtml({ item, html, type: 'small', addToCart: true, element: this });
                      return `<div class="slider__slide related-products-item" data-slider-slide>${productHtml}</div>`;
                    }).join('')}
                  </div>
                </div>
                ${sliderButtons ? `
                  <div class="slider__buttons">
                    <div class="slider__buttons-inner">
                      <button data-slider-prev class="slider__prev-button slider__prev-button--disabled" aria-label="Previous product">
                        <svg class="icon icon-caret icon--xs" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M5.52876 3.52861C5.26841 3.78896 5.26841 4.21107 5.52876 4.47141L9.05735 8.00001L5.52876 11.5286C5.26841 11.789 5.26841 12.2111 5.52876 12.4714C5.78911 12.7318 6.21122 12.7318 6.47157 12.4714L10.4716 8.47141C10.7319 8.21106 10.7319 7.78895 10.4716 7.52861L6.47157 3.52861C6.21122 3.26826 5.78911 3.26826 5.52876 3.52861Z" fill="currentColor"></path>
                        </svg>
                      </button>
                      <button data-slider-next class="slider__next-button" aria-label="Next product">
                        <svg class="icon icon-caret icon--xs" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M5.52876 3.52861C5.26841 3.78896 5.26841 4.21107 5.52876 4.47141L9.05735 8.00001L5.52876 11.5286C5.26841 11.789 5.26841 12.2111 5.52876 12.4714C5.78911 12.7318 6.21122 12.7318 6.47157 12.4714L10.4716 8.47141C10.7319 8.21106 10.7319 7.78895 10.4716 7.52861L6.47157 3.52861C6.21122 3.26826 5.78911 3.26826 5.52876 3.52861Z" fill="currentColor"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                ` : ''}
              </div>
            </div>
          </slider-component>
        `;

        // Insert the HTML directly
        container.insertAdjacentHTML('beforeend', sliderHtml);
      }

      /**
       * Custom fetch and render method for frequently bought together
       */
      async fetchAndRenderFrequentlyBoughtTogether(cartItemIds, productsEl, productsLoaderEl, productsNoResultsEl, recommendEl, options) {
        const { indexName, itemsToShow, randomize, confidenceScore, sliderButtons, slideMultipleSlides } = options;
        try {
          const timestamp = Date.now();
          let allItems = [];
          const seenProductIds = new Set(); // Track product IDs we've already added

          // Loop through cart items to get recommendations until we have enough
          for (let i = 0; i < cartItemIds.variant.length && allItems.length < itemsToShow; i++) {
            const variantId = cartItemIds.variant[i];

            const response = await this.recommendClient.getFrequentlyBoughtTogether([{
              indexName,
              objectID: variantId,
              maxRecommendations: 60,
              threshold: confidenceScore,
              queryParameters: {
                filters: "inventory_available:true"
              }
            }], {
              // Add cache busting headers
              headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
              }
            });

            let items = response.results[0].hits || [];

            // Sort the items by score
            items = items.sort((a, b) => b._score - a._score);

            // Filter out items already in cart and items we've already added
            items = items.filter(item => {
              // Exclude items already in cart
              if (cartItemIds && cartItemIds.product && cartItemIds.product.includes(item.id)) {
                return false;
              }
              // Exclude items we've already added from previous cart items
              if (seenProductIds.has(item.id)) {
                return false;
              }
              return true;
            });

            // Add new items to our collection
            items.forEach(item => {
              seenProductIds.add(item.id);
              allItems.push(item);
            });

            // Stop early if we have enough items
            if (allItems.length >= itemsToShow) {
              break;
            }
          }

          let items = allItems;

          // Sort all items by score (items from earlier cart items may have higher scores)
          items = items.sort((a, b) => b._score - a._score);

          // Save the items in the state
          this.state['frequently-bought-together'] = {
            items,
            timestamp: timestamp
          }

          // Ensure we have enough items after filtering
          if (items.length < itemsToShow) {
            // Not enough items available after filtering
          }

          // Limit the number of items
          items = items.slice(0, itemsToShow);

          // Always shuffle the items to get different results
          // Use a more sophisticated shuffle to ensure different results
          for (let i = items.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [items[i], items[j]] = [items[j], items[i]];
          }

          // Remove the loader and show/hide the no results message
          productsLoaderEl.classList.add('hidden');
          if (items.length === 0) {
            productsNoResultsEl.classList.remove('hidden');
          } else {
            recommendEl.classList.add('loaded')
            productsNoResultsEl.classList.add('hidden');
          }

          // Render products using custom approach
          this.renderFrequentlyBoughtTogether(items, productsEl, sliderButtons, slideMultipleSlides);

        } catch (error) {
          console.error('Error fetching frequently bought together products:', error);
          productsLoaderEl.classList.add('hidden');
          productsNoResultsEl.classList.remove('hidden');
        }
      }

      /**
       * Custom render method for frequently bought together
       */
      renderFrequentlyBoughtTogether(items, container, sliderButtons, slideMultipleSlides) {
        // Clear existing content
        container.innerHTML = '';

        // Create a proper HTML template function
        const html = (strings, ...values) => {
          return strings.reduce((result, string, i) => {
            return result + string + (values[i] || '');
          }, '');
        };

        // Create the slider structure
        const sliderHtml = `
          <slider-component class="algolia-recommend" data-options='{
            "moveMultipleSlides": ${slideMultipleSlides},
            "offset": 24,
            "mobileOffset": 24
          }'>
            <div class="slider bought-together-products">
              <div class="slider__container">
                <div class="slider__wrapper slider__wrapper--no-scroll-bar ${items.length <= 2 ? 'slider__wrapper--default-cursor' : ''}" data-scroller-wrapper>
                  <div class="slider__content slider__content--full bought-together-items" data-scroller-content>
                    ${items.map(item => {
                      const productHtml = productCardHtml({ item, html, type: 'small', addToCart: true, element: this });
                      return `<div class="slider__slide bought-together-item" data-slider-slide>${productHtml}</div>`;
                    }).join('')}
                  </div>
                </div>
                ${sliderButtons ? `
                  <div class="slider__buttons">
                    <div class="slider__buttons-inner">
                      <button data-slider-prev class="slider__prev-button slider__prev-button--disabled" aria-label="Previous product">
                        <svg class="icon icon-caret icon--xs" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M5.52876 3.52861C5.26841 3.78896 5.26841 4.21107 5.52876 4.47141L9.05735 8.00001L5.52876 11.5286C5.26841 11.789 5.26841 12.2111 5.52876 12.4714C5.78911 12.7318 6.21122 12.7318 6.47157 12.4714L10.4716 8.47141C10.7319 8.21106 10.7319 7.78895 10.4716 7.52861L6.47157 3.52861C6.21122 3.26826 5.78911 3.26826 5.52876 3.52861Z" fill="currentColor"></path>
                        </svg>
                      </button>
                      <button data-slider-next class="slider__next-button" aria-label="Next product">
                        <svg class="icon icon-caret icon--xs" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M5.52876 3.52861C5.26841 3.78896 5.26841 4.21107 5.52876 4.47141L9.05735 8.00001L5.52876 11.5286C5.26841 11.789 5.26841 12.2111 5.52876 12.4714C5.78911 12.7318 6.21122 12.7318 6.47157 12.4714L10.4716 8.47141C10.7319 8.21106 10.7319 7.78895 10.4716 7.52861L6.47157 3.52861C6.21122 3.26826 5.78911 3.26826 5.52876 3.52861Z" fill="currentColor"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                ` : ''}
              </div>
            </div>
          </slider-component>
        `;

        // Insert the HTML directly
        container.insertAdjacentHTML('beforeend', sliderHtml);
      }

      /**
       * Get sections to render for cart updates
       * @returns {Array} Array of section objects with id and selector properties
       */
      getSectionsToRender() {
        return [
          {
            id: 'cart-drawer',
            selector: '[data-cart-drawer]'
          },
          {
            id: 'cart-icon-bubble',
            selector: '[data-cart-icon-bubble]'
          }
        ];
      }

      /**
       * Get section Inner html
       * @param {string} html
       * @param {string} selector
       * @returns DOM
       */
      getSectionInnerHTML(html, selector) {
        return new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(selector).innerHTML;
      }

      initializeWishlist() {
        // Initialize wishlist functionality
        this.initWishlist();

        // Set up Swym callback for wishlist events
        this.setupSwymCallback();

        // Event delegation to handle clicks on wishlist buttons (only add once)
        if (!window.wishlistClickHandlerAdded) {
          window.wishlistClickHandlerAdded = true;
          document.addEventListener('click', (event) => {
            // Check if the clicked element is a wishlist button or inside one
            const wishlistButton = event.target.closest('.wishlist-trigger');
            if (wishlistButton) {
              event.preventDefault();
              event.stopPropagation();

              // Get all the data attributes from the button
              const productData = {
                empi: wishlistButton.getAttribute('data-empi'),
                epi: wishlistButton.getAttribute('data-epi'),
                dt: wishlistButton.getAttribute('data-dt'),
                du: wishlistButton.getAttribute('data-du'),
                ct: wishlistButton.getAttribute('data-ct'),
                pr: wishlistButton.getAttribute('data-pr'),
                stk: wishlistButton.getAttribute('data-stk'),
                iu: wishlistButton.getAttribute('data-iu')
              };

              // Call the Swym API to add the product to the wishlist
              this.addToWishlist(productData);
            }
          });
        }
      }

      // Get wishlist items and apply "swym-added" class to existing items
      async initWishlist() {
        let countIntervals = 0;
        return await new Promise((resolve, reject) => {
          const interval = setInterval(() => {
            countIntervals++;

            // Check if Wishlist app is initialized
            if (window?._swat && window?._swat?.fetch) {
              clearInterval(interval);

              // Fetch wishlist items and resolve results
              window._swat.fetch((results) => {

                // Save results
                this.wishlistItems = results;

                // Apply results on page load
                for (let result in results) {
                  const item = results[result];
                  if (!item.empi) {
                    return;
                  }
                  document.querySelectorAll(`.wishlist-trigger.product_${item.empi}`).forEach((button) => {
                    button.classList.add('swym-added');
                  });
                }

                resolve(results);
              });
            }

            // No wishlist app, throw error.
            if (countIntervals == 100) {
              clearInterval(interval);
              reject('No wishlist app');
            }
          }, 250);
        });
      }

      // Set up Swym callback for wishlist events
      setupSwymCallback() {
        // Prevent duplicate callback registration across all instances
        if (window.swymCallbackRegistered) {
          return;
        }
        window.swymCallbackRegistered = true;

        const swymCallbackFn = (swat) => {
          swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, function(event) {
            var product = event?.detail?.d;
            if (!product) {
              return false;
            }

            // Update the button state
            const button = document.querySelector(`.wishlist-trigger.product_${product.empi}`);
            if (button) {
              button.classList.add('swym-added');
            }

            // Notification delay
            setTimeout(() => {
              // Return if a notification is already showing
              if (document.querySelector('.swym-notifications-container.swym-showing')) {
                if (!document.querySelector('.swym-notifications-container.custom')) {
                  return true;
                }
              }

              // Else display a custom notification
              const notification = `
                <div class="swym-notifications-container custom swymTopLeft">
                  <a href="/pages/swym-wishlist" class="swym-notification-wishlist swym-clickable">
                    <div class="swym-product-tile">
                      <button class="swym-close-btn swym-is-button">
                        <svg class="swym-icon icon icon-close icon--xs" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" viewBox="0 0 24 24">
                          <title>Close</title>
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M18.1,17.1c0.3,0.3,0.3,0.8,0,1.1s-0.8,0.3-1.1,0L12,13.1l-5.1,5.1c-0.3,0.3-0.8,0.3-1.1,0
                          c-0.3-0.3-0.3-0.8,0-1.1l5.1-5.1L5.9,6.9c-0.3-0.3-0.3-0.8,0-1.1s0.8-0.3,1.1,0l5.1,5.1l5.1-5.1c0.3-0.3,0.8-0.3,1.1,0
                          c0.3,0.3,0.3,0.8,0,1.1l0,0L13.1,12L18.1,17.1z"></path>
                        </svg>
                      </button>
                      <img class="swym-image" src="${product.iu}" alt="">
                      <div class="swym-details-container">
                        <div class="swym-details" role="alert">
                          <strong>${product.dt}</strong> has been added to your wishlist successfully.
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
              `;

              const container = document.querySelector('.swym-wishlist-notifications-container');

              container.innerHTML = notification;

              const popup = container.querySelector('.swym-notifications-container');

              // animate in.
              setTimeout(() => {
                if (popup) {
                  popup.classList.add('swym-showing');
                }
              }, 250);

              // animate out.
              setTimeout(() => {
                if (popup) {
                  popup.classList.remove('swym-showing');
                }
              }, 6000);

              // animate out on click close btn
              container.querySelector('.swym-notifications-container .swym-close-btn ').addEventListener('click', (e) => {
                e.preventDefault();
                container.querySelector('.swym-notifications-container').classList.remove('swym-showing');
              });
            }, 250);
          });
        };

        if (!window.SwymCallbacks) {
          window.SwymCallbacks = [];
        }
        window.SwymCallbacks.push(swymCallbackFn);
      }

      addToWishlist(product) {
        // Check if Swym is available
        if (!window._swat) {
          return;
        }

        // Check if addToWishList method exists
        if (!window._swat.addToWishList) {
          return;
        }

        try {
          // Validate and fix product data with proper fallbacks
          const fixedProduct = {
            empi: parseInt(product.empi) || 0,
            epi: parseInt(product.epi) || 0,
            dt: product.dt || 'Unknown Product',
            du: product.du || window.location.origin,
            ct: product.ct || '',
            pr: parseFloat(product.pr) || 0,
            stk: parseInt(product.stk) || 0,
            iu: product.iu || '/assets/no-image.gif'
          };

          // Validate required fields
          if (!fixedProduct.empi || !fixedProduct.epi) {
            console.error('Invalid product data: missing empi or epi', product);
            return;
          }

          // Fix localhost URLs for Swym (replace with production domain)
          if (fixedProduct.du && (fixedProduct.du.includes('127.0.0.1:9292') || fixedProduct.du.includes('localhost'))) {
            fixedProduct.du = fixedProduct.du.replace(/http:\/\/127\.0\.0\.1:9292/g, 'https://farmer-gracy.myshopify.com');
            fixedProduct.du = fixedProduct.du.replace(/http:\/\/localhost:\d+/g, 'https://farmer-gracy.myshopify.com');
          }

          // Use Swym API to add product to wishlist with callback function (like working implementation)
          window._swat.addToWishList(
            fixedProduct,
            function(result) {
              // Update button state if needed
              const button = document.querySelector(`.wishlist-trigger.product_${fixedProduct.empi}`);
              if (button) {
                button.classList.add('swym-added');
              }
            },
            false
          );
        } catch (error) {
          console.error('Error calling Swym addToWishList:', error);
        }
      }
    }

    window.CartDrawerRecommend = CartDrawerRecommend;

    customElements.define('cart-drawer-recommend', CartDrawerRecommend);

  }

  if (!customElements.get('recommend-quickshop')) {
    class RecommendQuickshop extends HTMLElement {
      constructor() {
        super();

        /*
        * Check if algolia is done rendering the virtual DOM
        * Needed because algolia works with preact and sometimes the children nodes are not loaded yet
        */
        const checkAlgoliaRendering = () => {
          const button = this.querySelector('.product-card__quickshop-button');

          if (button) {
            this.initQuickshop();
          } else {
            setTimeout(checkAlgoliaRendering, 100);
          }
        };

        checkAlgoliaRendering();
      }

      initQuickshop() {
        const button = this.querySelector('.product-card__quickshop-button');
        const select = this.querySelector('.product-card__quickshop-select');

        if (!button || !select) {
          return;
        }

        // Store the selected variant ID
        let storedVariantId = null;

        // Handle button click - trigger select dropdown
        button.addEventListener('click', (e) => {
          e.preventDefault();
          select.focus();
          select.click();
        });

        // Handle select change - automatically add to cart when variant is selected
        select.addEventListener('change', () => {
          const selectedVariantId = select.value;
          storedVariantId = selectedVariantId; // Store the selected variant ID

          if (selectedVariantId) {
            this.addToCart(selectedVariantId);
          }
        });

        // Monitor select value changes
        let lastSelectValue = select.value;
        setInterval(() => {
          if (select.value !== lastSelectValue) {
            lastSelectValue = select.value;
          }
        }, 100);
      }

      async addToCart(variantId) {
        const button = this.querySelector('.product-card__quickshop-button');
        const originalText = button.textContent;

        // Update button state
        button.disabled = true;
        button.textContent = window.variantStrings?.adding || 'Adding...';

        try {
          // Get sections to fetch from the CartDrawerRecommend class
          const cartDrawerRecommend = document.querySelector('cart-drawer-recommend');
          const sectionsToFetch = cartDrawerRecommend ? cartDrawerRecommend.getSectionsToRender().map(section => section.id) : ['cart-drawer', 'cart-icon-bubble'];

          // Add to cart with sections
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', '1');
          formData.append('properties[_recommended]', 'true');
          formData.append('sections', sectionsToFetch.join(','));
          formData.append('sections_url', window.location.pathname);

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to add to cart: ${response.status} ${errorText}`);
          }

          const data = await response.json();

          // Update cart sections
          this.updateCartSections(data);

          // Refresh recommendations after cart update (with small delay to ensure cart is updated)
          setTimeout(() => {
            this.refreshRecommendations();
          }, 500);

          // Show success state
          button.textContent = window.variantStrings?.added || 'Added!';
          button.classList.add('success');

          // Reset button after delay
          setTimeout(() => {
            button.disabled = false;
            button.textContent = originalText;
            button.classList.remove('success');
          }, 2000);

        } catch (error) {
          console.error('Error adding to cart:', error);
          button.disabled = false;
          button.textContent = originalText;
        }
      }

      updateCartSections(data) {
        // Try to use the main cart drawer's renderContents method if available
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.renderContents && data.sections) {
          cartDrawer.renderContents(data);
          return;
        }

        // Fallback: manual section updates
        const cartDrawerRecommend = document.querySelector('cart-drawer-recommend');

        if (!cartDrawerRecommend) {
          return;
        }

        const sectionsToRender = cartDrawerRecommend.getSectionsToRender();

        sectionsToRender.forEach(section => {
          const element = document.querySelector(section.selector);

          if (element) {
            if (data.sections && data.sections[section.id]) {
              const newHTML = cartDrawerRecommend.getSectionInnerHTML(data.sections[section.id], section.selector);
              element.innerHTML = newHTML;
            }
          }
        });
      }

        async refreshRecommendations() {
          // Find all cart-drawer-recommend elements and fetch fresh recommendations
          const recommendElements = document.querySelectorAll('cart-drawer-recommend');

          for (const element of recommendElements) {
            // Get fresh cart data
            const cartItemIds = await element.getCartItemIds();

            if (!cartItemIds.variant.length) {
              continue;
            }

            // Force fresh fetch by clearing state and reinitializing
            element.state = {
              'related-products': { items: [], timestamp: null },
              'frequently-bought-together': { items: [], timestamp: null }
            };

            // Remove loaded class to force reinitialization
            element.classList.remove('loaded');

            // Reinitialize with fresh data
            await element.initRecommendations();
          }
        }
    }

    window.RecommendQuickshop = RecommendQuickshop;

    customElements.define('recommend-quickshop', RecommendQuickshop);

  }

})(window.algoliaShopify);
